import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Volume2, VolumeX } from 'lucide-react';

// Dark mob soundtrack — Sopranos-inspired noir jazz
// Slow piano, walking upright bass, brushed drums, moody atmosphere

const createMobEngine = (audioCtx) => {
  const masterGain = audioCtx.createGain();
  masterGain.gain.value = 0.3;
  masterGain.connect(audioCtx.destination);

  // Reverb — large dark room
  const convolver = audioCtx.createConvolver();
  const reverbLen = audioCtx.sampleRate * 2.5;
  const reverbBuf = audioCtx.createBuffer(2, reverbLen, audioCtx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const d = reverbBuf.getChannelData(ch);
    for (let i = 0; i < reverbLen; i++) {
      d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / reverbLen, 3);
    }
  }
  convolver.buffer = reverbBuf;
  const reverbGain = audioCtx.createGain();
  reverbGain.gain.value = 0.35;
  convolver.connect(reverbGain);
  reverbGain.connect(masterGain);

  const dryBus = audioCtx.createGain();
  dryBus.gain.value = 0.8;
  dryBus.connect(masterGain);
  dryBus.connect(convolver);

  // Piano — Rhodes-style electric piano with warmth
  const playPiano = (time, note, duration = 0.8, vel = 0.12) => {
    // Fundamental
    const osc1 = audioCtx.createOscillator();
    osc1.type = 'sine';
    osc1.frequency.value = note;

    // Slight detune for warmth
    const osc2 = audioCtx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.value = note * 1.001;

    // Bell harmonic
    const osc3 = audioCtx.createOscillator();
    osc3.type = 'sine';
    osc3.frequency.value = note * 4;

    const gain = audioCtx.createGain();
    gain.gain.setValueAtTime(vel, time);
    gain.gain.setValueAtTime(vel * 0.8, time + 0.01);
    gain.gain.exponentialRampToValueAtTime(vel * 0.4, time + duration * 0.3);
    gain.gain.exponentialRampToValueAtTime(0.001, time + duration);

    const bellGain = audioCtx.createGain();
    bellGain.gain.setValueAtTime(vel * 0.15, time);
    bellGain.gain.exponentialRampToValueAtTime(0.001, time + 0.15);

    const filter = audioCtx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(2000, time);
    filter.frequency.exponentialRampToValueAtTime(800, time + duration);
    filter.Q.value = 1;

    osc1.connect(filter);
    osc2.connect(filter);
    filter.connect(gain);
    osc3.connect(bellGain);
    bellGain.connect(gain);
    gain.connect(dryBus);

    osc1.start(time); osc1.stop(time + duration);
    osc2.start(time); osc2.stop(time + duration);
    osc3.start(time); osc3.stop(time + duration);
  };

  // Upright bass — warm, round, walking
  const playBass = (time, note, duration = 0.5) => {
    const osc = audioCtx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = note;

    const osc2 = audioCtx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.value = note;

    const gain = audioCtx.createGain();
    // Plucked attack
    gain.gain.setValueAtTime(0.35, time);
    gain.gain.setValueAtTime(0.28, time + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.15, time + duration * 0.4);
    gain.gain.exponentialRampToValueAtTime(0.001, time + duration);

    const filter = audioCtx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 350;
    filter.Q.value = 2;

    osc.connect(filter);
    osc2.connect(filter);
    filter.connect(gain);
    gain.connect(dryBus);

    osc.start(time); osc.stop(time + duration);
    osc2.start(time); osc2.stop(time + duration);
  };

  // Brushed cymbal ride — soft, swishing
  const playRide = (time, accent = false) => {
    const noise = audioCtx.createBufferSource();
    const dur = accent ? 0.2 : 0.08;
    const buf = audioCtx.createBuffer(1, audioCtx.sampleRate * dur, audioCtx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1);
    noise.buffer = buf;

    const filter = audioCtx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = accent ? 6000 : 8000;
    filter.Q.value = 0.5;

    const gain = audioCtx.createGain();
    gain.gain.setValueAtTime(accent ? 0.06 : 0.035, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + dur);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(dryBus);
    noise.start(time);
  };

  // Brush swirl on snare — soft texture
  const playBrush = (time) => {
    const noise = audioCtx.createBufferSource();
    const dur = 0.3;
    const buf = audioCtx.createBuffer(1, audioCtx.sampleRate * dur, audioCtx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1);
    noise.buffer = buf;

    const filter = audioCtx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 2500;
    filter.Q.value = 0.8;

    const gain = audioCtx.createGain();
    gain.gain.setValueAtTime(0, time);
    gain.gain.linearRampToValueAtTime(0.05, time + 0.05);
    gain.gain.linearRampToValueAtTime(0.03, time + 0.15);
    gain.gain.exponentialRampToValueAtTime(0.001, time + dur);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(dryBus);
    noise.start(time);
  };

  // Soft kick — felt beater, not punchy
  const playKick = (time) => {
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.frequency.setValueAtTime(80, time);
    osc.frequency.exponentialRampToValueAtTime(40, time + 0.12);
    gain.gain.setValueAtTime(0.2, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.25);
    osc.connect(gain);
    gain.connect(dryBus);
    osc.start(time); osc.stop(time + 0.25);
  };

  // Atmosphere pad — dark strings
  const playAtmosphere = (time, notes, duration = 4) => {
    notes.forEach(note => {
      const osc = audioCtx.createOscillator();
      const osc2 = audioCtx.createOscillator();
      const gain = audioCtx.createGain();

      osc.type = 'sine';
      osc.frequency.value = note;
      osc2.type = 'sine';
      osc2.frequency.value = note * 2.01; // Slight shimmer

      gain.gain.setValueAtTime(0, time);
      gain.gain.linearRampToValueAtTime(0.025, time + 1);
      gain.gain.linearRampToValueAtTime(0.02, time + duration - 1);
      gain.gain.linearRampToValueAtTime(0, time + duration);

      const filter = audioCtx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 600;

      osc.connect(filter);
      osc2.connect(gain);
      filter.connect(gain);
      gain.connect(convolver);

      osc.start(time); osc.stop(time + duration);
      osc2.start(time); osc2.stop(time + duration);
    });
  };

  return { playPiano, playBass, playRide, playBrush, playKick, playAtmosphere, masterGain };
};

const GameMusic = () => {
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.3);
  const audioCtxRef = useRef(null);
  const engineRef = useRef(null);
  const timerRef = useRef(null);
  const barRef = useRef(0);

  // Slow, brooding tempo — Sopranos feel
  const BPM = 62;
  const BEAT_TIME = 60 / BPM;
  const BAR_TIME = BEAT_TIME * 4;

  // Dm / Am / Bbmaj7 / Gm — dark mob jazz progression
  const CHORD_PROGRESSION = [
    { bass: [73.42, 87.31, 73.42, 65.41], piano: [[293.66, 349.23, 440.00]], name: 'Dm' },
    { bass: [55.00, 65.41, 73.42, 65.41], piano: [[220.00, 329.63, 392.00]], name: 'Am' },
    { bass: [58.27, 69.30, 73.42, 58.27], piano: [[233.08, 293.66, 349.23, 440.00]], name: 'Bbmaj7' },
    { bass: [49.00, 58.27, 65.41, 55.00], piano: [[196.00, 233.08, 293.66]], name: 'Gm' },
  ];

  // Piano voicings — sparse, noir
  const PIANO_PATTERNS = [
    // Pattern A: sparse hits
    [{ beat: 0, vel: 0.12 }, { beat: 2.5, vel: 0.08 }],
    // Pattern B: walking response
    [{ beat: 0.5, vel: 0.09 }, { beat: 1.5, vel: 0.07 }, { beat: 3, vel: 0.10 }],
    // Pattern C: single heavy note
    [{ beat: 0, vel: 0.14 }],
    // Pattern D: bluesy
    [{ beat: 0, vel: 0.11 }, { beat: 1, vel: 0.06 }, { beat: 2, vel: 0.09 }, { beat: 3.5, vel: 0.07 }],
  ];

  const scheduleBar = useCallback((ctx, engine, startTime, barNum) => {
    const chordIdx = barNum % CHORD_PROGRESSION.length;
    const chord = CHORD_PROGRESSION[chordIdx];
    const pianoPattern = PIANO_PATTERNS[barNum % PIANO_PATTERNS.length];

    // Walking bass — one note per beat
    chord.bass.forEach((note, i) => {
      if (note > 0) {
        const dur = i < 3 ? BEAT_TIME * 0.9 : BEAT_TIME * 0.7;
        engine.playBass(startTime + i * BEAT_TIME, note, dur);
      }
    });

    // Piano — sparse chord hits
    pianoPattern.forEach(({ beat, vel }) => {
      const notes = chord.piano[0];
      const t = startTime + beat * BEAT_TIME;
      // Play chord notes individually with slight stagger
      notes.forEach((note, i) => {
        engine.playPiano(t + i * 0.015, note, BEAT_TIME * 1.5, vel);
      });
    });

    // Ride cymbal — jazz pattern (1, 2-and, 3, 4-and)
    engine.playRide(startTime, true);
    engine.playRide(startTime + BEAT_TIME * 1.5, false);
    engine.playRide(startTime + BEAT_TIME * 2, true);
    engine.playRide(startTime + BEAT_TIME * 3.5, false);

    // Brush on 2 and 4
    if (barNum % 2 === 0) {
      engine.playBrush(startTime + BEAT_TIME * 1);
      engine.playBrush(startTime + BEAT_TIME * 3);
    } else {
      engine.playBrush(startTime + BEAT_TIME * 1);
      engine.playBrush(startTime + BEAT_TIME * 2.5);
    }

    // Soft kick on 1 (not every bar)
    if (barNum % 2 === 0) {
      engine.playKick(startTime);
    }
    // Ghost kick
    if (barNum % 4 === 2) {
      engine.playKick(startTime + BEAT_TIME * 2.5);
    }

    // Atmosphere — every 4 bars
    if (barNum % 4 === 0) {
      const atmNotes = chordIdx <= 1
        ? [146.83, 174.61] // Dm drone
        : [116.54, 146.83]; // Bb drone
      engine.playAtmosphere(startTime, atmNotes, BAR_TIME * 4);
    }
  }, [BEAT_TIME, BAR_TIME]);

  const startMusic = useCallback(() => {
    if (audioCtxRef.current) return;
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === 'suspended') ctx.resume();
    audioCtxRef.current = ctx;
    const engine = createMobEngine(ctx);
    engineRef.current = engine;
    engine.masterGain.gain.value = volume;

    let nextBarTime = ctx.currentTime + 0.1;
    barRef.current = 0;

    const schedule = () => {
      while (nextBarTime < ctx.currentTime + 0.5) {
        scheduleBar(ctx, engine, nextBarTime, barRef.current);
        nextBarTime += BAR_TIME;
        barRef.current++;
      }
      timerRef.current = setTimeout(schedule, 100);
    };
    schedule();
    setPlaying(true);
  }, [volume, BAR_TIME, scheduleBar]);

  const stopMusic = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (audioCtxRef.current) {
      audioCtxRef.current.close();
      audioCtxRef.current = null;
    }
    engineRef.current = null;
    setPlaying(false);
  }, []);

  const toggleMusic = useCallback(() => {
    if (playing) stopMusic();
    else startMusic();
  }, [playing, startMusic, stopMusic]);

  useEffect(() => {
    if (engineRef.current) {
      engineRef.current.masterGain.gain.value = volume;
    }
  }, [volume]);

  useEffect(() => {
    return () => stopMusic();
  }, [stopMusic]);

  return (
    <div className="flex items-center gap-2" data-testid="game-music-controls">
      <button onClick={toggleMusic}
        className={`p-2 rounded-lg border transition-all ${
          playing
            ? 'bg-[#C9A84C]/10 border-[#C9A84C]/30 text-[#C9A84C]'
            : 'bg-[#111820] border-[#1A2535] text-[#E8E0D0]/30 hover:text-[#E8E0D0]/60'
        }`}
        data-testid="music-toggle"
        title={playing ? 'Mute music' : 'Play music'}>
        {playing ? <Volume2 size={16} /> : <VolumeX size={16} />}
      </button>
      {playing && (
        <input type="range" min="0" max="100" value={Math.round(volume * 100)}
          onChange={e => setVolume(e.target.value / 100)}
          className="w-16 h-1 accent-[#C9A84C] opacity-50 hover:opacity-100 transition-opacity cursor-pointer"
          data-testid="music-volume"
        />
      )}
    </div>
  );
};

export default GameMusic;
